import { useHeaderItems } from "@/constants/header-items";
import {
  Bars3Icon,
  XMarkIcon,
  ArrowRightEndOnRectangleIcon,
} from "@heroicons/react/20/solid";
import { cn } from "@/lib/utils";
import { Disclosure } from "@headlessui/react";

import Logo from "@/assets/woba.svg";
import {
  Fragment,
  MouseEventHandler,
  ElementRef,
  ComponentPropsWithoutRef,
  forwardRef,
} from "react";
import { Link, useLocation } from "react-router-dom";
import { useLogout } from "@/features/signin/hooks/use-logout.hook";

import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu";
import { sendTrackDataServer } from "@/features/analytics/utils/serverTrackData";
import { DEFAULT_ROUTES } from "@/constants/routes";

const ListItem = forwardRef<
  ElementRef<"a">,
  ComponentPropsWithoutRef<"a"> & { isNew?: boolean }
>(({ className, title, children, isNew, ...props }, ref) => {
  return (
    <li>
      <NavigationMenuLink asChild className="p-0">
        <a
          ref={ref}
          className={cn(
            "block select-none space-y-1 rounded-md py-2 px-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
            className,
          )}
          {...props}
        >
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium leading-none">{title}</span>
            {isNew && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-[#F9E10D]">
                novo
              </span>
            )}
          </div>
          <p className="line-clamp-2 text-sm leading-snug text-muted-foreground">
            {children}
          </p>
        </a>
      </NavigationMenuLink>
    </li>
  );
});
ListItem.displayName = "ListItem";

export const Header = () => {
  const logout = useLogout();
  const headerItems = useHeaderItems();

  const handleLogout: MouseEventHandler = () => {
    logout.handleLogout();
  };
  const location = useLocation();

  return (
    <Disclosure
      as="nav"
      className="bg-white border-b-2 border-gray-100 relative z-50"
    >
      {({ open }) => (
        <div className="mx-auto px-2 sm:px-6 lg:px-8">
          <div className="relative flex h-16 items-center justify-between">
            <div className="absolute inset-y-0 left-0 flex items-center sm:hidden">
              {/* Mobile menu button*/}
              <Disclosure.Button className="relative inline-flex items-center justify-center rounded-md p-2 text-gray-400 hover:bg-gray-700 hover:text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white">
                <span className="absolute -inset-0.5" />
                <span className="sr-only">Open main menu</span>
                {open ? (
                  <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                ) : (
                  <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                )}
              </Disclosure.Button>
            </div>

            <div className="flex flex-1 items-center justify-center sm:items-stretch sm:justify-start">
              <div className="flex flex-shrink-0 items-center">
                <img className="h-6 w-auto" src={Logo} alt="" />
              </div>

              <div className="hidden sm:ml-6 sm:block">
                <div className="flex space-x-4">
                  <NavigationMenu>
                    <NavigationMenuList>
                      {headerItems.map((item, index) => (
                        <Fragment key={index}>
                          {item.submenu.length === 0 ? (
                            <NavigationMenuLink
                              asChild
                              className={navigationMenuTriggerStyle()}
                            >
                              <Link to={item.link}>{item.title}</Link>
                            </NavigationMenuLink>
                          ) : (
                            <NavigationMenuItem>
                              <NavigationMenuTrigger>
                                {item.title}
                              </NavigationMenuTrigger>
                              <NavigationMenuContent>
                                <ul className="flex flex-col gap-1 p-3  w-[300px]">
                                  {item.submenu.map((subItem, index) => (
                                    <ListItem
                                      onClick={() => {
                                        if (
                                          subItem.link ===
                                          DEFAULT_ROUTES.PRIVATE_OFFICE_NEW_CREATE
                                        ) {
                                          sendTrackDataServer({
                                            eventName:
                                              "CREATE_NEW_ROOM_CLICKED",
                                            data: { entry_point: "menu" },
                                            url: location.pathname,
                                            productType: "PO",
                                          });
                                        }
                                      }}
                                      key={index}
                                      href={subItem.link}
                                      title={subItem.title}
                                      target={subItem.target}
                                      isNew={subItem.isNew}
                                    ></ListItem>
                                  ))}
                                </ul>
                              </NavigationMenuContent>
                            </NavigationMenuItem>
                          )}
                        </Fragment>
                      ))}
                    </NavigationMenuList>
                  </NavigationMenu>
                </div>
              </div>
            </div>
            <div className="absolute inset-y-0 right-0 flex items-center pr-2 sm:static sm:inset-auto sm:ml-6 sm:pr-0">
              <button
                type="button"
                className="relative rounded-full bg-white p-1 text-black hover:text-gray-40"
                onClick={handleLogout}
              >
                <span className="absolute -inset-1.5" />
                <span className="sr-only">Sair</span>
                <ArrowRightEndOnRectangleIcon
                  className="h-6 w-6"
                  aria-hidden="true"
                />
              </button>
            </div>
          </div>
        </div>
      )}
    </Disclosure>
  );
};
